import React, { useRef, useState } from "react";
import { Box, Button, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import zIndex from "@mui/material/styles/zIndex";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as CheckDefIcon } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckFillIcon } from "assets/images/icons/checkbox_fiilled.svg";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton } from "components/buttons/SolidButton";
import ImportView from "./ImportView";

const csvSample = [
  ["IP Address", "Username", "Password", "Spine/Leaf Role (Optional)"],
  ["192.168.1.10", "admin", "password", "Spine"],
  ["192.168.1.11", "admin", "password", "Leaf"],
];

const UploadView = ({ form, onClose, onApply }) => {
  const theme = useTheme();
  const viewRef = useRef(null);
  const [showImportView, setShowImportView] = useState(false);
  const [devices, setDevices] = useState([]);
  const [totalCsvCount, setTotalCsvCount] = useState(0);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const viewWidth = viewRef.current?.offsetWidth || 0;
  const viewHeight = viewRef.current?.offsetHeight || 0;

  const downloadCSVSample = () => {
    const content = `data:text/csv;charset=utf-8,${encodeURIComponent(
      csvSample.map((row) => row.join(",")).join("\n"),
    )}`;
    const link = document.createElement("a");
    link.href = content;
    link.download = "data_center_switch_sample.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closeImportView = () => setShowImportView(false);

  return (
    <>
      <Box ref={viewRef} sx={{ position: "relative", p: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            Upload Data Center Switches
          </Typography>
          <Button
            onClick={onClose}
            sx={{
              minWidth: 36,
              p: 0,
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": { backgroundColor: theme.palette.primary.transparent },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mb: 1 }}>
          <OutlineRectangleButton startIcon={<AddIcon />} height="32px" onClick={downloadCSVSample}>
            Download CSV Sample
          </OutlineRectangleButton>
        </Box>

        <Box sx={{ border: `1px solid ${theme.palette.primary.borderGreyPrimary}`, borderRadius: "4px", mx: 1 }}>
          <Box sx={{ ...form.getTableStyle(), height: "300px", padding: "0 10px" }}>
            <TableContainer
              component={Paper}
              sx={{ "&.MuiPaper-root": { boxShadow: "none", border: "none" } }}
              style={{ overflowY: "auto" }}
            >
              <Table sx={{ borderCollapse: "collapse", "& td, & th": { border: "none" } }}>
                <TableHead>
                  <TableRow style={{ borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}` }}>
                    {csvSample[0].map((header) => (
                      <TableCell key={header} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.map((device, index) => (
                    <TableRow key={`${device.ipAddress}-${index}`}>
                      <TableCell>{device.ipAddress}</TableCell>
                      <TableCell>{device.username}</TableCell>
                      <TableCell>{device.password}</TableCell>
                      <TableCell>{device.role}</TableCell>
                    </TableRow>
                  ))}
                  {devices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: "center", py: 2 }}>
                        No Entry in the table
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, mx: 2 }}>
          <Typography sx={form.getLabelStyle()}>
            Devices: {devices.length} / {totalCsvCount}
          </Typography>
          <OutlineRectangleButton startIcon={<AddIcon />} height="32px" onClick={() => setShowImportView(true)}>
            Upload
          </OutlineRectangleButton>
        </Box>

        <Box sx={{ ml: 1, mt: 1, display: "flex", alignItems: "center" }}>
          <Checkbox
            icon={<CheckDefIcon style={{ width: "24px", height: "24px" }} />}
            checkedIcon={<CheckFillIcon style={{ width: "24px", height: "24px" }} />}
            checked={replaceExisting}
            onChange={(event) => setReplaceExisting(event.target.checked)}
          />
          <Typography component="span" sx={{ fontSize: theme.typography.customFontSize[2] }}>
            Replace the existing Devices in the list.
          </Typography>
        </Box>

        <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
          <OutlineButton onClick={onClose} sx={{ marginRight: "20px" }} width="100px" height="32px">
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={() => onApply(devices, replaceExisting)}
            width="100px"
            height="32px"
            disabled={devices.length === 0}
          >
            Import
          </SolidButton>
        </Box>

        {showImportView && (
          <Box
            style={{
              ...form.getMaskStyle(),
              width: viewWidth + 40 + "px",
              height: viewHeight + 60 + "px",
              transform: "translateY(-20px) translateX(-20px)",
            }}
          />
        )}
      </Box>

      {showImportView && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: viewWidth + 40 + "px",
            transform: `translateY(${viewHeight / 10}px)`,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <ImportView
              form={form}
              onClose={closeImportView}
              onImport={(newDevices, count) => {
                setDevices(newDevices);
                setTotalCsvCount(count);
                closeImportView();
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default React.memo(UploadView);
